// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect, useSelector, useDispatch } from "react-redux";
import * as R from "ramda";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Flex, IconButton, Portal } from "@chakra-ui/react";

import {
  ChartList as StyledChartList,
  FooterButton,
  Header,
  ChartCell,
  ChartBody,
  Description as StyledDescription,
  List,
  CloseButton,
  Report as StyledReport,
  Title as StyledTitle,
} from "../styles";
import * as styles from "../styles";
import Image from "./Image";
import Title from "./Title";
import Description from "./Description";
import Report from "./Report";
import Icon from "src/icons";
import { Overlay } from "src/styles/modal";
import {
  setNewChartDialogAttributes,
  deleteChart,
  showEditChartModal,
  closeNewChartDialog,
  fetchChart,
} from "src/actions/chart";
import {
  getAllCharts,
  getInstanceFilterId,
  getInstanceReportId,
  getChartsByReport,
  getAllChartIds,
} from "src/reducers";
import { getFilteredDashboardCharts } from "src/reducers/combinedSelectors";
import { Input } from "src/styles/input";
import { updateInstanceFilter } from "src/actions/workflows";
import { editChart } from "src/actions/chart";
import { comparisonCharts } from "src/constants/charts.js";
import DeleteModal from "src/components/Manage/Reports/Instances/Chart/New/ComparisonChart/Builder/Series/DeleteModal";
import useClickoutside from "src/hooks/useClickoutside";

import appLocation from "src/constants/location";

type Props = {
  header?: string,
  newChart?: boolean,
  handleClick: Function,
  _setNewChartDialogAttributes: Function,
  _showEditChartModal: Function,
  handleClose: Function,
  location: string,
  _updateInstanceFilter: Function,
  _closeNewChartDialog: Function,
  _searchChart: Function,
  showAll?: boolean,
};

const ChartList = ({
  handleClick,
  _setNewChartDialogAttributes,
  _showEditChartModal,
  _updateInstanceFilter,
  _closeNewChartDialog,
  handleClose,
  location,
  header = i18n.t(k.CHARTS_FOR_THIS_REPORT),
  newChart = true,
  showAll = false,
}: Props) => {
  const dispatch = useDispatch();
  const parentRef = useRef(null);
  const searchRef = useRef(null);
  const comparisonChartList = R.values(comparisonCharts);
  useClickoutside({
    outerRef: parentRef,
    handleClose,
    disableEnter: true,
    ignoreSelectors: ['[aria-modal="true"]', '[role="dialog"]'],
  });

  const allCharts = useSelector(({ app }) => getAllCharts(app));
  const workflowId = useSelector(({ app }) => getInstanceFilterId(app));
  const reportId = useSelector(({ app }) => getInstanceReportId(app));

  const charts = useSelector(({ app }) => {
    if (showAll) {
      return getAllChartIds(app);
    } else if (location === appLocation.manageView) {
      return getChartsByReport(app, Number(reportId));
    } else if (location === appLocation.dashboard) {
      return getFilteredDashboardCharts(app);
    }
  });
  const [filteredCharts, setFilteredCharts] = useState([]);

  useEffect(() => {
    setFilteredCharts(charts);
  }, [charts]);

  const handleSearch = useCallback(
    (event: any) => {
      const query = event.target.value;
      const filtered =
        charts.filter((chart) =>
          chart.title.toLowerCase().includes(query.toLowerCase()),
        ) || [];
      setFilteredCharts(filtered);
    },
    [filteredCharts],
  );

  const closeModal = useCallback(
    (event: any) => {
      if (event.key === "Escape") {
        handleClose();
      }
    },
    [handleClose],
  );

  useLayoutEffect(() => {
    setTimeout(() => {
      if (searchRef && searchRef.current) {
        searchRef.current.focus();
      }
    }, 0);
  }, []);

  const handleNewChart = useCallback(() => {
    if (newChart) {
      _updateInstanceFilter({
        chartId: null,
      });
    }

    _closeNewChartDialog();

    _setNewChartDialogAttributes({
      show: true,
    });

    handleClose();
  }, [
    newChart,
    handleClose,
    _setNewChartDialogAttributes,
    _updateInstanceFilter,
    _closeNewChartDialog,
  ]);

  const handleEdit = useCallback(
    (event: SyntheticMouseEvent<HTMLDivElement>, chartId: number) => {
      event.preventDefault();
      event.stopPropagation();
      const chartDetails = allCharts[chartId];
      const { type, reportId } = chartDetails;
      const isComparisonChart = comparisonChartList.includes(type);
      if (isComparisonChart) {
        dispatch(
          editChart({
            id: chartId,
            type,
            reportId,
            workflowId,
          }),
        );
        dispatch(fetchChart(chartId));
      } else {
        _showEditChartModal(chartId);
      }

      handleClose();
    },
    [_showEditChartModal],
  );

  const handleChartJson = useCallback(
    (id: number) => {
      if (newChart) {
        _updateInstanceFilter({
          chartId: null,
        });
      }
      _closeNewChartDialog();

      handleClick(id);
      handleClose();
    },
    [newChart, handleClose, handleClick],
  );

  return (
    <Portal>
      <Overlay />
      <StyledChartList ref={parentRef}>
        <Header>
          {header}
          <CloseButton type="button" onClick={handleClose}>
            <Icon type="close" />
          </CloseButton>
        </Header>
        <Input
          type="text"
          placeholder={i18n.t(k.SEARCH_CHARTS)}
          ref={searchRef}
          onChange={handleSearch}
          autoFocus
        />

        <List>
          {filteredCharts.map((chart) => (
            <ChartCell key={chart.id} onClick={() => handleChartJson(chart.id)}>
              <Image id={chart.id} />
              <ChartBody>
                <Flex sx={styles.IconWrapper}>
                  <IconButton
                    onClick={(event: any) => handleEdit(event, chart.id)}
                    sx={styles.Icon}
                    icon={<Icon type="edit" />}
                  />

                  <DeleteModal
                    title={chart.title}
                    handleDelete={() => dispatch(deleteChart(chart.id))}
                  />
                </Flex>

                <StyledTitle>
                  <Title id={chart.id} />
                </StyledTitle>

                <StyledDescription>
                  <span>{i18n.t(k.DESCRIPTION1)} </span>
                  <Description id={chart.id} />
                </StyledDescription>

                <StyledReport>
                  <span>{i18n.t(k.REPORT)}</span>
                  <div>
                    <Report id={chart.id} />
                  </div>
                </StyledReport>
              </ChartBody>
            </ChartCell>
          ))}
        </List>
        {newChart && (
          <FooterButton type="button" onClick={handleNewChart}>
            {i18n.t(k.CREATE_NEW_CHART)}
          </FooterButton>
        )}
      </StyledChartList>
    </Portal>
  );
};

export default connect(null, {
  _setNewChartDialogAttributes: setNewChartDialogAttributes,
  _showEditChartModal: showEditChartModal,
  _updateInstanceFilter: updateInstanceFilter,
  _closeNewChartDialog: closeNewChartDialog,
})(ChartList);
